import React from 'react';
import { View, ScrollView, Pressable, Modal } from 'react-native';
import { __WEB__, THEME, FONT, FONT_SIZE, COLOR, __ANDROID__ } from '../../constants/styles';
import ActivityOverlay from './activity_overlay';
import ErrorView from './form/error_view';
import OPComponent from './op_component';
import ActionSheet from './platform_wrappers/actionsheet';
import SuccessView from './display/container_success';
import Navigation from '../../navigation';
import Confirm from './confirm';
import { SafeAreaView } from 'react-native-safe-area-context';

import ActionSheetContext from './action_sheet_context';
import ConfirmDialogContext from './confirm_dialog_context';

class Container extends OPComponent {
  constructor(props) {
    super(props);
    this.state = {
      actionSheet: null,
    };
    this.scrollViewRef = React.createRef();
    this.actionSheetRef = React.createRef();
  }
  componentDidMount() {
    this.setState({ actionSheet: this.actionSheetRef.current });
  }
  getActionSheetRef() {
    return this.actionSheetRef.current;
  }

  _renderError() {
    if (this.props.errorMessage) {
      return <ErrorView errorText={this.props.errorMessage} />;
    }
    return null;
  }

  _renderSuccess() {
    if (this.props.successMessage) {
      return (
        <SuccessView
          notificationStyle="success"
          successMessage={this.props.successMessage}
          dismissMessage={this.props.onDismissMessage}
        />
      );
    }
  }
  _renderNotification() {
    if (!__WEB__ || !this.props.notificationOpts) return null;
    return (
      <View style={{ position: 'absolute', top: 0, right: 0, left: 0, zIndex: 10 }}>
        <SuccessView
          notificationStyle="notification"
          successMessage={this.props.notificationOpts.textNode || this.props.notificationOpts.text}
          dismissMessage={this.props.notificationOpts.onClose}
          onPress={this.props.notificationOpts.onPress}
        />
      </View>
    );
  }

  _renderContent() {
    return <View style={[{ flex: 1 }, this.props.innerContentStyle]}>{this.props.children}</View>;
  }

  scrollToEnd(animated = true) {
    if (__WEB__) {
      const el = this.scrollViewRef.current;
      if (el) {
        el.scrollLeft = 0;
        el.scrollTop = el.scrollHeight;
      }
      return;
    }
    this.scrollViewRef.current?.scrollToEnd?.({ animated });
  }

  scrollToTop({ animated = true }) {
    if (__WEB__) {
      const el = this.scrollViewRef.current;
      if (el) {
        el.scrollLeft = 0;
        el.scrollTop = 0;
      }
      return;
    }
    this.scrollViewRef.current?.scrollTo?.({ x: 0, y: 0, animated });
  }

  _renderScrollview() {
    const {
      keyboardAvoidingScrollView,
      scrollView,
      scrollViewStyle,
      contentContainerStyle,
      keyboardShouldPersistTaps,
    } = this.props;

    if (keyboardAvoidingScrollView) {
      return (
        <ScrollView
          keyboardShouldPersistTaps={keyboardShouldPersistTaps || 'never'}
          contentContainerStyle={contentContainerStyle}
          automaticallyAdjustKeyboardInsets={true}
          automaticallyAdjustContentInsets={false}
          ref={this.scrollViewRef}
        >
          {this._renderContent()}
        </ScrollView>
      );
    }

    if (scrollView) {
      if (__WEB__) {
        return (
          <View ref={this.scrollViewRef} style={[{ flex: 1, overflow: 'auto' }, scrollViewStyle]}>
            <View style={contentContainerStyle}>{this._renderContent()}</View>
          </View>
        );
      }

      return (
        <ScrollView
          keyboardShouldPersistTaps={this.props.keyboardShouldPersistTaps || 'never'}
          alwaysBounceVertical={false}
          keyboardDismissMode={this.props.keyboardDismissMode || 'on-drag'}
          contentContainerStyle={contentContainerStyle}
          ref={this.scrollViewRef}
        >
          {this._renderContent()}
        </ScrollView>
      );
    }

    return this._renderContent();
  }

  _renderActivityOverlay() {
    const { renderActivityOverlay } = this.props;

    if (!renderActivityOverlay) return null;

    return <ActivityOverlay sizeToView={true} color="black" />;
  }

  _renderSuccessOverlay() {
    const { renderSuccessOverlay } = this.props;
    if (!renderSuccessOverlay) return null;

    return <ActivityOverlay sizeToView={true} success={true} />;
  }

  _renderPlainOverlay() {
    const { renderPlainOverlay } = this.props;

    if (!renderPlainOverlay) return null;

    return <ActivityOverlay sizeToView={true} noSpinner={true} />;
  }

  _renderConfirmationOpts(confirmationOpts) {
    return <Confirm confirmationOpts={confirmationOpts} />;
  }

  _renderConfirmationPopup(confirmationOpts) {
    if (__WEB__) return null;
    if (!confirmationOpts) confirmationOpts = this.props.confirmationOpts;
    if (!confirmationOpts) return null;
    const renderedContents = this._renderConfirmationOpts(confirmationOpts);

    return (
      <Modal animationType="fade" transparent={true} onRequestClose={confirmationOpts?.onCancelled}>
        <Pressable
          onPress={confirmationOpts?.onCancelled}
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            backgroundColor: COLOR.overlay,
            justifyContent: 'center',
            paddingTop: 0,
            alignItems: 'center',
          }}
        >
          {renderedContents}
        </Pressable>
      </Modal>
    );
  }

  _renderContainer() {
    const WrapperComp = __WEB__ ? View : SafeAreaView;
    return (
      <WrapperComp
        style={[this.props.style || THEME.container]}
        edges={['bottom', 'left', 'right', 'top']}
      >
        {this._renderError()}
        {this._renderSuccess()}
        {this._renderNotification()}
        <View style={{ flex: 1 }}>{this._renderScrollview()}</View>
        {this.props.stickyFooter || null}
        {this._renderActivityOverlay()}
        {this._renderSuccessOverlay()}
        {this._renderPlainOverlay()}
        {this._renderConfirmationPopup()}
      </WrapperComp>
    );
  }

  componentDidUpdate(prevProps) {
    if (this.props.scrollTo && this.props.scrollTo !== prevProps.scrollTo) {
      this.scrollViewRef.current?.scrollTo(this.props.scrollTo);
    }
    if (__WEB__ && this.props.confirmationOpts !== prevProps.confirmationOpts) {
      const { confirmationOpts } = this.props;
      if (!confirmationOpts) {
        Navigation.showConfirmation(null);
      } else {
        const renderedContents = this._renderConfirmationOpts(confirmationOpts);
        Navigation.showConfirmation({ ...confirmationOpts, renderedContents });
      }
    }
  }

  openConfirmDialog = (confirmationOpts) => {
    if (__WEB__) {
      Navigation.showConfirmation({
        ...confirmationOpts,
        renderedContents: this._renderConfirmationOpts(confirmationOpts),
      });
    } else {
      this._renderConfirmationPopup(confirmationOpts);
    }
  };
  closeConfirmDialog = () => {
    if (__WEB__) {
      Navigation.showConfirmation(null);
    } else {
      // this._renderConfirmationPopup(null);
    }
  };

  render() {
    const { actionSheet } = this.props;

    if (actionSheet) {
      return (
        <ActionSheet ref={this.actionSheetRef}>
          <ActionSheetContext.Provider value={{ actionSheet: this.state.actionSheet }}>
            <ConfirmDialogContext.Provider value={{ open: this.openConfirmDialog }}>
              {this._renderContainer()}
            </ConfirmDialogContext.Provider>
          </ActionSheetContext.Provider>
        </ActionSheet>
      );
    } else {
      return (
        <ConfirmDialogContext.Provider
          value={{ open: this.openConfirmDialog, close: this.closeConfirmDialog }}
        >
          {this._renderContainer()}
        </ConfirmDialogContext.Provider>
      );
    }
  }
}

export default Container;
